import { MessageOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import * as api from '../lib/api';
import { STATUS_CONFIG } from '../lib/taskStatus';
import type { Task } from '../lib/api';
import { useTaskStore } from '../stores/useTaskStore';
import TaskRowActions from './TaskRowActions';
import surface from './ProjectSurface.module.css';
import styles from './PersonalTasksPage.module.css';

type TaskSectionName = 'in_progress' | 'attention' | 'completed';

const groups: Array<{ key: TaskSectionName; label: string }> = [
  { key: 'in_progress', label: 'In progress' },
  { key: 'attention', label: 'Needs attention' },
  { key: 'completed', label: 'Completed' },
];

function taskSection(task: Task): TaskSectionName {
  if (task.status === 'in_progress') return 'in_progress';
  if (task.status === 'done' || task.status === 'cancelled') return 'completed';
  return 'attention';
}

function responsibilityRank(task: Task) {
  const relationship = task.relationship;
  if (relationship?.assignee || relationship?.creator) return 0;
  if (relationship?.participant) return 1;
  if (relationship?.owned_agent) return 2;
  return 3;
}

function relationshipLabel(task: Task) {
  const relationship = task.relationship;
  if (!relationship) return null;
  const labels = [
    relationship.assignee && 'Assigned to you',
    relationship.creator && 'Created by you',
    relationship.participant && 'You participate',
    relationship.owned_agent && 'Your agent',
  ].filter(Boolean);
  return labels.join(' · ');
}

export default function PersonalTasksPage({
  activeThreadId,
  onTaskClick,
}: {
  activeThreadId?: string;
  onTaskClick: (task: Task) => void;
}) {
  // The inbox remembers only which authorized Tasks it loaded. The normalized
  // task store is the single source of entity data for list, thread, mutation,
  // and realtime updates; lanes are always derived from the live status.
  const [loadedTaskIds, setLoadedTaskIds] = useState<string[]>([]);
  const taskById = useTaskStore((state) => state.byTaskId);
  const [ready, setReady] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  useEffect(() => {
    let stale = false;
    api.fetchMyTasks().then(({ tasks }) => {
      if (stale) return;
      for (const task of tasks) useTaskStore.getState().upsertTask(task);
      setLoadedTaskIds(tasks.map((task) => task.id));
    }).finally(() => { if (!stale) setReady(true); });
    return () => { stale = true; };
  }, []);
  const tasks = useMemo(
    () => loadedTaskIds
      .map((id) => taskById[id])
      .filter((task): task is Task => !!task)
      .filter((task) => {
        const text = `${task.title} ${task.channel?.guild_name ?? ''} ${task.channel?.channel_name ?? ''}`.toLowerCase();
        return (!query || text.includes(query.toLowerCase())) && (filter === 'all' || task.status === filter);
      })
      .sort((a, b) => responsibilityRank(a) - responsibilityRank(b) || b.updated_at - a.updated_at),
    [loadedTaskIds, taskById, query, filter],
  );
  const updateTask = (updated: Task) => useTaskStore.getState().upsertTask(updated);
  const removeTask = (taskId: string) => {
    useTaskStore.getState().removeTask(taskId);
    setLoadedTaskIds((ids) => ids.filter((id) => id !== taskId));
  };

  if (!ready) return <div className={surface.loading}>Loading your tasks…</div>;
  return (
    <main className={surface.pageRoot}>
      <header className={`${surface.pageContainer} ${surface.pageHeader} ${styles.header}`}>
        <div>
          <span className={surface.eyebrow}>Personal</span>
          <h1 className={surface.pageTitle}>My Tasks</h1>
        </div>
      </header>
      <div className={`${surface.pageContainer} ${styles.toolbar}`}>
        <input
          className={styles.search}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search tasks…"
          aria-label="Search tasks"
        />
        <div className={styles.filters}>
          {[
            ['all', 'All'],
            ['open', 'Open'],
            ['in_progress', 'In progress'],
            ['in_review', 'In review'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={`${styles.filter} ${filter === key ? styles.filterActive : ''}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {query ? (
        <TaskList
          label="Search results"
          rows={tasks}
          activeThreadId={activeThreadId}
          onTaskClick={onTaskClick}
          onUpdated={updateTask}
          onDeleted={removeTask}
        />
      ) : (
        groups.map(({ key, label }) => {
          const rows = tasks.filter((task) => taskSection(task) === key);
          return (
            <section className={`${surface.pageContainer} ${surface.section} ${styles.section}`} key={label}>
              <h2>
                {label}
                {rows.length ? ` · ${rows.length}` : ''}
              </h2>
              <div className={surface.borderedList}>
                {rows.length ? (
                  rows.map((task) => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      active={task.thread_id === activeThreadId}
                      onClick={onTaskClick}
                      onUpdated={updateTask}
                      onDeleted={removeTask}
                    />
                  ))
                ) : (
                  <div className={surface.listEmpty}>No tasks here.</div>
                )}
              </div>
            </section>
          );
        })
      )}
    </main>
  );
}

function TaskList({
  label,
  rows,
  activeThreadId,
  onTaskClick,
  onUpdated,
  onDeleted,
}: {
  label: string;
  rows: Task[];
  activeThreadId?: string;
  onTaskClick: (task: Task) => void;
  onUpdated: (task: Task) => void;
  onDeleted: (taskId: string) => void;
}) {
  return (
    <section className={`${surface.pageContainer} ${surface.section} ${styles.section}`}>
      <h2>
        {label}
        {rows.length ? ` · ${rows.length}` : ''}
      </h2>
      <div className={surface.borderedList}>
        {rows.length ? (
          rows.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              active={task.thread_id === activeThreadId}
              onClick={onTaskClick}
              onUpdated={onUpdated}
              onDeleted={onDeleted}
            />
          ))
        ) : (
          <div className={surface.listEmpty}>No matching tasks.</div>
        )}
      </div>
    </section>
  );
}

function TaskRow({
  task,
  active,
  onClick,
  onUpdated,
  onDeleted,
}: {
  task: Task;
  active: boolean;
  onClick: (task: Task) => void;
  onUpdated: (task: Task) => void;
  onDeleted: (taskId: string) => void;
}) {
  const cfg = STATUS_CONFIG[task.status];
  const relationship = relationshipLabel(task);
  return (
    <div className={`${surface.listRow} ${surface.listRowInteractive} ${styles.row} ${active ? surface.listRowActive : ''}`}>
      <button
        type="button"
        className={styles.rowOpener}
        onClick={() => task.thread_id && onClick(task)}
        disabled={!task.thread_id}
        aria-label={task.thread_id ? `Open ${task.title}` : `${task.title} has no task thread`}
      >
        <span className={styles.status} style={{ background: cfg?.color ?? '#a1a1aa' }} />
        <span className={styles.copy}>
          <strong>{task.title}</strong>
          <small>
            {task.channel
              ? `${task.channel.guild_name} · #${task.channel.channel_name}`
              : 'Personal task'}
            {relationship && (
              <>
                <span aria-hidden="true"> · </span>
                <span className={styles.relationship}>{relationship}</span>
              </>
            )}
          </small>
        </span>
        <span className={styles.meta}>{cfg?.label ?? task.status}</span>
        {task.thread_id && <MessageOutlined className={styles.thread} />}
      </button>
      <TaskRowActions task={task} onUpdated={onUpdated} onDeleted={onDeleted} />
    </div>
  );
}
